import React, { FC } from 'react';
import { useTranslations } from '@lang';
import { ParsedJourneyParams } from '@store/yourJourney/selectors';
import { ListPage } from '@features/List';
import { useRouter } from 'shared-routing';
import { Routes } from '@common/constants';
import { Moment } from 'moment';
import { parseDateToString } from '@utils/helpers';
import { PromoRateCode } from 'shared-types/Common';
import { useRatesErrorMessage } from '@common/hooks/useRatesErrorMessage';
import { useRatePageSettings, useRates } from '@features/rates';
import { useModuleStatuses } from '@features/properties';
import { useRoomPageSettings } from '@features/rooms';

type RatesProps = {
  journeyParams: ParsedJourneyParams;
  changeJourneyParam: (paramName: string, value?: string | Moment | number | PromoRateCode) => void;
  propertyId: number;
  language: string;
};

export const Rates: FC<RatesProps> = ({ journeyParams, changeJourneyParam, language }) => {
  const router = useRouter();
  const { t } = useTranslations();
  const { content: roomsPageContent, isFetching: isFetchingRoomsContent } = useRoomPageSettings(
    language,
    t('common.rate')
  );

  const { content: ratesPageContent, isFetching: isFetchingRatesContent } = useRatePageSettings(
    language,
    t('common.rate')
  );

  const {
    statuses: { isRoomsEnabled },
  } = useModuleStatuses();
  const acceptPromoRateCode = isRoomsEnabled;

  const adults = journeyParams.adults ?? 0;
  const children = journeyParams.children ?? 0;
  const childAges = journeyParams.childAges;
  const fromDate = parseDateToString(journeyParams.fromDate);
  const toDate = parseDateToString(journeyParams.toDate);
  const promoRateCode =
    typeof router.query.promoRateCode === 'string'
      ? router.query.promoRateCode
      : journeyParams.promoRateCode;
  const rooms = journeyParams.rooms || [{ roomNumber: 1, adults, children }];

  const { rates, isFetching, error } = useRates({
    fromDate,
    toDate,
    adults,
    children,
    promoRateCode,
    childAges,
    rooms,
  });
  const rateListItems = rates
    .map((rate) => ({
      ...rate,
      linkRoute: Routes.RATE_DETAIL,
      linkParams: {
        id: rate.id,
        fromDate,
        toDate,
        adults,
        children,
        promoRateCode,
      },
    }))
    .filter((rate) => !rate.unAvailable);

  const errorMessage = useRatesErrorMessage(error);

  const onChangeJourneyParams = (params) => {
    changeJourneyParam('fromDate', params.fromDate);
    changeJourneyParam('spaDate', params.fromDate);
    changeJourneyParam('toDate', params.toDate);
    changeJourneyParam(
      'adults',
      (params.rooms || []).reduce((acc, room) => acc + room.adults, 0) ?? params.adults
    );
    changeJourneyParam(
      'children',
      (params.rooms || []).reduce((acc, room) => acc + room.children, 0) ?? params.children
    );
    changeJourneyParam('promoRateCode', params.promoRateCode);
    changeJourneyParam('childAges', params.childAges);
    changeJourneyParam('rooms', params.rooms);
    const { promoRateCode, childAges: _childAges, rooms: _rooms, ...otherParams } = params;
    const { rateCode } = router.query;
    router.push({
      pathname: Routes.RATE_LIST,
      query: {
        ...otherParams,
        ...(promoRateCode ? { promoRateCode } : undefined),
        ...(rateCode ? { rateCode } : undefined),
      } as any,
    });
  };

  return (
    <ListPage
      journeyParams={journeyParams}
      onChangeJourneyParams={onChangeJourneyParams}
      title={ratesPageContent.title ?? roomsPageContent.title}
      description={ratesPageContent.description ?? roomsPageContent.description}
      items={rateListItems}
      isFetching={isFetching || isFetchingRoomsContent || isFetchingRatesContent}
      errorMessage={errorMessage}
      acceptPromoRateCode={acceptPromoRateCode}
    />
  );
};
